/**
 * The two password dialogs. They look similar and are not the same thing.
 *
 * - **`ChangePasswordModal`** — a user changes their own password and must
 *   prove they know the current one. That proof is what stops a stolen session
 *   cookie from becoming permanent account takeover, and it shares the login
 *   throttle, so a wrong current password can lock the account out for five
 *   minutes. The server issues a fresh cookie on success, so the user is not
 *   logged out of the tab they are standing in — but every *other* session
 *   dies, which is the whole point.
 *
 * - **`ResetPasswordModal`** — an admin sets someone else's password without
 *   the old one. Deliberately: it is the tool for a forgotten password and for
 *   locking out a compromised account, so it also clears that user's lockout.
 *
 * The policy check (`passwordProblem`) is duplicated from `auth/users.py` for
 * instant feedback only. The server checks it again on every path.
 */
import { useEffect, useState } from 'react'
import { Alert, Button, Group, PasswordInput, Stack, Text } from '@mantine/core'

import { Modal } from './AppModal'
import { useMutation } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'

import { ApiError, api } from '../api/client'
import { MIN_PASSWORD_LENGTH, passwordProblem } from '../auth/password'

export function ChangePasswordModal({
  opened, onClose,
}: { opened: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (opened) { setCurrent(''); setNext(''); setConfirm(''); setError(null) }
  }, [opened])

  const change = useMutation({
    mutationFn: () =>
      api.post<{ detail: string }>('/auth/me/password', {
        current_password: current, new_password: next,
      }),
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'הסיסמה עודכנה. כל הכניסות האחרות בוטלו.',
      })
      onClose()
    },
    // 429 means the shared login throttle tripped on wrong current passwords.
    // The server's message says so; showing it verbatim is more useful than
    // "failed".
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'שינוי הסיסמה נכשל.'),
  })

  function submit() {
    setError(null)
    if (!current) return setError('נא להזין את הסיסמה הנוכחית.')
    const problem = passwordProblem(next)
    if (problem) return setError(problem)
    if (next !== confirm) return setError('הסיסמאות אינן תואמות.')
    if (next === current) return setError('הסיסמה החדשה זהה לנוכחית.')
    change.mutate()
  }

  return (
    <Modal opened={opened} onClose={onClose} title="שינוי סיסמה">
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <PasswordInput
          label="סיסמה נוכחית"
          required
          value={current}
          onChange={(e) => setCurrent(e.currentTarget.value)}
        />
        <PasswordInput
          label="סיסמה חדשה"
          required
          description={`לפחות ${MIN_PASSWORD_LENGTH} תווים, ולפחות תו אחד שאינו אות.`}
          value={next}
          onChange={(e) => setNext(e.currentTarget.value)}
        />
        <PasswordInput
          label="אימות סיסמה חדשה"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
        />
        <Text size="xs" c="dimmed">
          שינוי הסיסמה מנתק כל מכשיר אחר שמחובר בחשבון זה. החלון הנוכחי יישאר
          מחובר.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button loading={change.isPending} onClick={submit}>עדכן סיסמה</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export function ResetPasswordModal({
  user, onClose,
}: { user: { id: number; username: string } | null; onClose: () => void }) {
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const opened = user !== null

  useEffect(() => {
    if (opened) { setNext(''); setConfirm(''); setError(null) }
  }, [opened])

  const reset = useMutation({
    mutationFn: () =>
      api.patch<{ detail: string }>(`/users/${user!.id}/password`, { password: next }),
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: `הסיסמה של ${user?.username} אופסה — הסשנים שלו בוטלו.`,
      })
      onClose()
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'איפוס הסיסמה נכשל.'),
  })

  function submit() {
    setError(null)
    const problem = passwordProblem(next)
    if (problem) return setError(problem)
    if (next !== confirm) return setError('הסיסמאות אינן תואמות.')
    reset.mutate()
  }

  return (
    <Modal opened={opened} onClose={onClose} title={`איפוס סיסמה — ${user?.username ?? ''}`}>
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <Alert color="yellow" variant="light">
          איפוס אינו דורש את הסיסמה הקודמת. כל הסשנים של המשתמש יבוטלו מיד,
          וגם נעילה בעקבות ניסיונות כניסה כושלים תוסר.
        </Alert>
        <PasswordInput
          label="סיסמה חדשה"
          required
          description={`לפחות ${MIN_PASSWORD_LENGTH} תווים, ולפחות תו אחד שאינו אות.`}
          value={next}
          onChange={(e) => setNext(e.currentTarget.value)}
        />
        <PasswordInput
          label="אימות סיסמה"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button color="orange" loading={reset.isPending} onClick={submit}>
            אפס סיסמה
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
